/**
 * The Knowledge Archive graph's zoom readout — ROUTE_INVENTORY §2.10 #25.
 *
 * Extracted from `MindspaceGraph` so it can be storied against plain props.
 * Inside the graph it is unreachable at a chosen scale: the only way to make
 * the canvas read 40% is to drive a wheel gesture through d3-zoom and hope the
 * momentum lands there.
 *
 * ## It is a button, and that is the whole design
 *
 * A percentage on its own would be a readout nobody needs — people do not zoom
 * to a number. What makes it worth the corner is that it is also the way BACK:
 * pan far enough into an 800-concept graph and every node is off screen, with
 * no edge, no scrollbar and no landmark to say which way home is. This is the
 * one control that always returns you to the whole graph, and the percentage is
 * what tells you that you are lost enough to want it.
 *
 * So the accessible name carries both halves — the current value and the
 * action — because "140%" alone tells a screen-reader user nothing about what
 * pressing it does.
 */

interface GraphZoomBadgeProps {
  /** Current canvas scale, 1 = 100%. */
  zoom: number;
  /** Fit every node back into view. */
  onFit: () => void;
}

export function GraphZoomBadge({ zoom, onFit }: GraphZoomBadgeProps) {
  const percent = Math.round(zoom * 100);
  return (
    <button
      type='button'
      onClick={onFit}
      // Positioned by the toolbar it sits in (`GraphControls`).
      className='inline-flex h-8 min-w-[3.25rem] items-center justify-center px-2 font-mono text-xs font-medium tabular-nums text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring'
      aria-label={`Current zoom ${percent} percent. Click to fit graph to view.`}
      title='Click to fit graph to view'
    >
      {percent}%
    </button>
  );
}
